import { useState, useEffect, useCallback } from 'react';
import {
  offlineStorage,
  UPLOAD_MESSAGES,
  type SurveyUploadState,
} from '@/services/offline-storage';
import { useToast } from '@/hooks/use-toast';

interface UseOfflineStorageReturn {
  isOfflineMode: boolean;
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: string | null;
  /** Set when changes saved on this device can't be read at all. */
  storageProblem: string | null;
  saveSurvey: (
    customerId: number,
    installationId: number | undefined,
    surveyId: number | undefined,
    data: any,
    baseServerUpdatedAt?: string | null
  ) => string;
  getUploadState: (customerId: number | null | undefined) => SurveyUploadState;
  markSavedToOffice: (
    customerId: number,
    savedFrom: string,
    serverUpdatedAt: string | null,
    dataWithPendingPhotos: Record<string, any> | null,
    surveyId?: number
  ) => void;
  uploadMyVersionAnyway: (customerId: number) => void;
  /** This page will upload the customer's device copy itself; the queue skips it. */
  claim: (customerId: number) => void;
  release: (customerId: number) => void;
  syncNow: () => Promise<void>;
  storageInfo: {
    used: number;
    surveyCount: number;
    photoCount: number;
    oldestSurvey: string | null;
  };
}

export function useOfflineStorage(): UseOfflineStorageReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [storageProblem, setStorageProblem] = useState<string | null>(null);
  const [uploadStates, setUploadStates] = useState<Map<number, SurveyUploadState>>(
    () => new Map()
  );
  const [storageInfo, setStorageInfo] = useState({
    used: 0,
    surveyCount: 0,
    photoCount: 0,
    oldestSurvey: null as string | null,
  });
  const { toast } = useToast();

  const updateStatus = useCallback(() => {
    try {
      const status = offlineStorage.getSyncStatus();
      setIsOnline(navigator.onLine);
      setIsSyncing(status.isSyncing);
      setPendingCount(status.pendingCount);
      setLastSyncTime(status.lastSyncTime || null);
      setStorageInfo(offlineStorage.getStorageInfo());
      setUploadStates(offlineStorage.getUploadStates());
      setStorageProblem(null);
    } catch {
      setIsSyncing(false);
      setUploadStates(new Map());
      setStorageProblem(UPLOAD_MESSAGES.unreadable);
    }
  }, []);

  useEffect(() => {
    const handleConnectivity = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', handleConnectivity);
    window.addEventListener('offline', handleConnectivity);
    const unsubscribe = offlineStorage.subscribe(updateStatus);
    updateStatus();

    return () => {
      window.removeEventListener('online', handleConnectivity);
      window.removeEventListener('offline', handleConnectivity);
      unsubscribe();
    };
  }, [updateStatus]);

  const saveSurvey = useCallback(
    (
      customerId: number,
      installationId: number | undefined,
      surveyId: number | undefined,
      data: any,
      baseServerUpdatedAt?: string | null
    ): string => {
      try {
        return offlineStorage.saveSurvey(
          customerId,
          installationId,
          surveyId,
          data,
          baseServerUpdatedAt
        );
      } catch (error) {
        console.error('Error saving survey offline:', error);
        toast({
          title: 'Save Failed',
          description:
            error instanceof Error ? error.message : 'Failed to save survey on this device.',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  // Reads the snapshot taken on the last queue change, so it is cheap to call every render.
  const getUploadState = useCallback(
    (customerId: number | null | undefined): SurveyUploadState =>
      (customerId ? uploadStates.get(customerId) : undefined) ?? { state: 'none' },
    [uploadStates]
  );

  const markSavedToOffice = useCallback(
    (
      customerId: number,
      savedFrom: string,
      serverUpdatedAt: string | null,
      dataWithPendingPhotos: Record<string, any> | null,
      surveyId?: number
    ) => {
      try {
        offlineStorage.markSavedToOffice(
          customerId,
          savedFrom,
          serverUpdatedAt,
          dataWithPendingPhotos,
          surveyId
        );
      } catch (error) {
        console.warn('[useOfflineStorage] Could not tidy local copy:', error);
      }
    },
    []
  );

  const uploadMyVersionAnyway = useCallback(
    (customerId: number) => {
      try {
        offlineStorage.uploadMyVersionAnyway(customerId);
      } catch (error) {
        toast({
          title: 'Upload failed',
          description:
            error instanceof Error ? error.message : 'Changes on this device could not be read.',
          variant: 'destructive',
        });
      }
    },
    [toast]
  );

  const claim = useCallback((customerId: number) => offlineStorage.claim(customerId), []);
  const release = useCallback((customerId: number) => offlineStorage.release(customerId), []);

  const syncNow = useCallback(async () => {
    if (!navigator.onLine) return;
    await offlineStorage.syncAllPending({ force: true });
  }, []);

  // Storage-pressure warning only; nothing is ever deleted here.
  useEffect(() => {
    offlineStorage.manageStorageQuota();
    const interval = setInterval(() => offlineStorage.manageStorageQuota(), 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return {
    isOfflineMode: !isOnline,
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncTime,
    storageProblem,
    saveSurvey,
    getUploadState,
    markSavedToOffice,
    uploadMyVersionAnyway,
    claim,
    release,
    syncNow,
    storageInfo,
  };
}
